import type { JSX } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { api, onSessionExpired, refreshAccessToken, tokenStore } from '@/lib/api'
import { ApiError } from '@/lib/api-error'
import { describeError } from '@/lib/problem-message'
import { getWsClient, initWsClient } from '@/lib/ws'
import { onCommandFailure } from '@/lib/ws-cache'
import { useSessionStore } from '@/stores/session'
import { UNDO_TOAST_MS, useToastStore } from '@/stores/toasts'
import { createSocketFactory } from '@/lib/socket-factory'
import { createAppRouter } from './router'

/**
 * Корень приложения: провайдеры, bootstrap сессии и подключение WS.
 *
 * Порядок важен. Сначала «тихий» refresh: refresh-cookie httpOnly, поэтому
 * узнать, жива ли сессия, можно только запросом (US-1 AC5). Только после этого
 * поднимаем роутер — иначе гард увёл бы на /login при каждом F5.
 */

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Данные обновляются по WS, поэтому агрессивный refetch не нужен.
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (attempt, error) => {
          // Нет смысла ретраить «нет прав» и «не найдено».
          if (error instanceof ApiError && error.status < 500) return false
          return attempt < 2
        }
      },
      mutations: { retry: false }
    }
  })
}

export function App(): JSX.Element {
  const [queryClient] = useState(createQueryClient)
  const router = useMemo(() => createAppRouter(queryClient), [queryClient])
  const [ready, setReady] = useState(false)

  const setSession = useSessionStore(state => state.setSession)
  const clear = useSessionStore(state => state.clear)

  useEffect(() => {
    let cancelled = false

    const bootstrap = async (): Promise<void> => {
      try {
        await refreshAccessToken()
        const me = await api.auth.me()
        if (!cancelled) setSession(me)
      } catch {
        // Нет живой сессии — это норма для первого визита.
        if (!cancelled) clear()
      } finally {
        if (!cancelled) setReady(true)
      }
    }

    void bootstrap()
    return () => {
      cancelled = true
    }
  }, [setSession, clear])

  // Сессия умерла посреди работы → чистим состояние и уводим на логин.
  useEffect(() => {
    onSessionExpired(() => {
      clear()
      getWsClient()?.disconnect()
      void router.navigate({ to: '/login' })
    })
    return () => onSessionExpired(null)
  }, [clear, router])

  return (
    <QueryClientProvider client={queryClient}>
      <WsBridge queryClient={queryClient} />
      {ready ? <RouterProvider router={router} /> : <BootSplash />}
    </QueryClientProvider>
  )
}

function BootSplash(): JSX.Element {
  const { t } = useTranslation()
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg">
      <p role="status" className="text-base text-fg-muted">
        {t('auth.checkingSession')}
      </p>
    </div>
  )
}

/**
 * Держит WS-соединение синхронным с сессией: появился токен — подключаемся,
 * пропал — рвём. Отдельным компонентом, чтобы подписка на store не
 * перерисовывала роутер.
 */
function WsBridge({ queryClient }: { queryClient: QueryClient }): null {
  const status = useSessionStore(state => state.status)
  const { t } = useTranslation()
  const push = useToastStore(state => state.push)
  const initialized = useRef(false)

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true
    initWsClient({ queryClient, factory: createSocketFactory(), refreshToken: refreshAccessToken })
    onCommandFailure(event => {
      push({
        message: t('toast.commandFailed', {
          reason: event.error ?? describeError(new Error(t('common.unknown')), t).detail
        }),
        tone: 'danger',
        timeoutMs: UNDO_TOAST_MS
      })
    })
  }, [queryClient, push, t])

  useEffect(() => {
    const client = getWsClient()
    if (client === null) return
    const token = tokenStore.get()
    if (status === 'authenticated' && token !== null) {
      client.connect(token)
      return
    }
    if (status === 'anonymous') client.disconnect()
  }, [status])

  return null
}
